import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReportListRow from './ReportListRow';
import { BRAND } from '../../theme/brandTheme';

const VISIBLE_DEFAULT = 8;

/** Grouped report list — modern card rows with column headers */
const BrandReportsList = ({
  reports,
  activeDropdown,
  apiLoading,
  user,
  onToggleDropdown,
  onStatusChange,
  onReportDetail,
  onDeleteReport,
  hideBrandHeader = false,
  expandAllTests = false,
}) => {
  const [expandedBrands, setExpandedBrands] = useState({});

  const isBrandExpanded = (brandId) => expandAllTests || expandedBrands[brandId];

  const toggleBrandExpanded = (brandId) => {
    setExpandedBrands((prev) => ({ ...prev, [brandId]: !prev[brandId] }));
  };

  return (
    <Stack spacing={3} id="brandList">
      {reports.map((brand) => {
        const expanded = isBrandExpanded(brand._id);
        const tests = brand.tests || [];
        const visibleTests = expanded ? tests : tests.slice(0, VISIBLE_DEFAULT);
        const hiddenCount = tests.length - VISIBLE_DEFAULT;

        return (
          <Box
            key={brand._id}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${BRAND.borderLight}`,
              bgcolor: `${BRAND.purple}03`,
              overflow: 'hidden',
            }}
          >
            {!hideBrandHeader && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  px: 2.5,
                  py: 2,
                  bgcolor: '#fff',
                  borderBottom: `1px solid ${BRAND.borderLight}`,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {brand.logo && (
                    <Box
                      component="img"
                      src={brand.logo}
                      alt=""
                      sx={{ width: 36, height: 36, objectFit: 'contain' }}
                    />
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.purple, lineHeight: 1.2 }}>
                      {brand.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                      {brand.stats?.toplam ?? 0} rapor
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <StatChip label="Canlı" value={brand.stats?.canli} color={BRAND.green} />
                  <StatChip label="Taslak" value={brand.stats?.taslak} color="#64748B" />
                  <StatChip label="Durduruldu" value={brand.stats?.durduruldu} color="#D97706" />
                  <Button
                    component={RouterLink}
                    to={`/dashboard/brand/${brand._id}`}
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ textTransform: 'none', color: BRAND.purple, fontWeight: 600, ml: 1 }}
                  >
                    Marka Sayfası
                  </Button>
                </Stack>
              </Box>
            )}

            {tests.length > 0 && (
              <Box sx={{ px: { xs: 1.5, md: 2 }, py: 2 }}>
                {/* Column headers — desktop only */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: 'minmax(0, 1fr) 120px 130px 140px auto',
                    gap: 2,
                    px: 2.5,
                    pb: 1,
                  }}
                >
                  {['Rapor', 'Durum', 'Sonuç', 'Tarih', ''].map((label) => (
                    <Typography
                      key={label || 'actions'}
                      variant="caption"
                      sx={{
                        color: BRAND.textMuted,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontSize: 11,
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>

                <Stack spacing={1.25}>
                  {visibleTests.map((test, idx) => (
                    <ReportListRow
                      key={test.id || `test-${idx}`}
                      test={test}
                      brandName={hideBrandHeader ? undefined : brand.name}
                      user={user}
                      activeDropdown={activeDropdown}
                      apiLoading={apiLoading}
                      onToggleDropdown={onToggleDropdown}
                      onStatusChange={onStatusChange}
                      onReportDetail={onReportDetail}
                      onDeleteReport={onDeleteReport}
                    />
                  ))}
                </Stack>

                {!expandAllTests && hiddenCount > 0 && (
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => toggleBrandExpanded(brand._id)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: expanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    }
                    sx={{
                      mt: 1.5,
                      textTransform: 'none',
                      color: BRAND.purple,
                      fontWeight: 600,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: '#fff',
                      border: `1px dashed ${BRAND.borderLight}`,
                      '&:hover': { bgcolor: `${BRAND.purple}06` },
                    }}
                  >
                    {expanded
                      ? 'Daha az göster'
                      : `${hiddenCount} rapor daha göster (${tests.length} toplam)`}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const StatChip = ({ label, value, color }) => (
  <Chip
    size="small"
    label={`${label}: ${value ?? 0}`}
    sx={{
      height: 26,
      fontWeight: 600,
      fontSize: 12,
      bgcolor: `${color}12`,
      color,
      border: `1px solid ${color}33`,
    }}
  />
);

export default BrandReportsList;
